import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Search, Wrench, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { cn } from 'lib/utils';
import { useGetToolsInfinite } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';
import { useConnectorTabTools } from 'pages/tools/use-connector-tab-tools';
import { useConnectorToolLogo } from 'pages/tools/use-connector-tool-logo';
import { isOpenConnectorEnabled } from 'config/feature-flags';

type AgentConfigToolType = 'api' | 'mcp_server' | 'composio' | 'open_connector';

type AgentConfigAddToolModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedToolIds: string[];
  onAddTool: (tool: ITool) => void;
  onRemoveTool: (toolId: string) => void;
};

function ConnectorToolLogo({
  tool,
  logoClass,
  idleClass,
}: {
  tool: ITool;
  logoClass: string;
  idleClass: string;
}) {
  const logo = useConnectorToolLogo(tool);

  return (
    <div
      className={cn(
        'flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl',
        logo ? logoClass : idleClass
      )}
    >
      {logo ? (
        <img src={logo} alt={tool.name} className="h-full w-full object-contain" />
      ) : (
        <Wrench className="h-5 w-5" />
      )}
    </div>
  );
}

function ToolRowSkeleton({ fieldClass }: { fieldClass: string }) {
  return (
    <div className={cn('flex items-center gap-3 rounded-2xl border p-4', fieldClass)}>
      <Skeleton className="h-11 w-11 shrink-0 rounded-2xl" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-48" />
      </div>
      <Skeleton className="h-8 w-16 rounded-full" />
    </div>
  );
}

export function AgentConfigAddToolModal({
  open,
  onOpenChange,
  selectedToolIds,
  onAddTool,
  onRemoveTool,
}: AgentConfigAddToolModalProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const activeTabText = theme === 'dark' ? '!text-true-white' : undefined;
  const [toolType, setToolType] = useState<AgentConfigToolType>('composio');
  const [searchQuery, setSearchQuery] = useState('');
  const listContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetToolsInfinite(
    {
      tool_type: toolType,
      page_size: 20,
    },
    { enabled: open, scope: 'agents-v2-config-modal' }
  );

  const showConnectorLogos = toolType === 'composio' || toolType === 'open_connector';
  const pageTools = useMemo(() => data?.pages.flatMap((page) => page.tools) ?? [], [data?.pages]);
  const { tools, isLoading: isPromotedLoading } = useConnectorTabTools(toolType, pageTools, open);

  const filteredTools = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return tools;
    return tools.filter((tool) => tool.name.toLowerCase().includes(query));
  }, [searchQuery, tools]);

  const selectedTools = tools.filter((tool) => selectedToolIds.includes(tool.id));
  const selectedCountForCurrentTab = selectedTools.length;

  useEffect(() => {
    const scrollContainer = listContainerRef.current;
    const loadMoreEl = loadMoreRef.current;
    if (!open || !scrollContainer || !loadMoreEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainer,
        rootMargin: '200px',
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreEl);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage, tools.length, isLoading, toolType]);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setToolType('composio');
      setSearchQuery('');
    }
    onOpenChange(nextOpen);
  };

  const emptyMessage = searchQuery
    ? toolType === 'mcp_server'
      ? t('NO_MCP_SERVERS_FOUND_MATCHING_SEARCH') || 'No MCP servers found matching your search'
      : toolType === 'open_connector'
        ? t('TOOLS_NO_OPEN_CONNECTOR_FOUND') || 'No open connector tools found'
        : t('NO_TOOLS_FOUND_MATCHING_SEARCH') || 'No tools found matching your search'
    : toolType === 'mcp_server'
      ? t('NO_MCP_SERVERS_AVAILABLE') || 'No MCP servers available'
      : toolType === 'composio'
        ? t('NO_CONNECTORS_AVAILABLE') || 'No Connectors available'
        : toolType === 'open_connector'
          ? t('NO_OPEN_CONNECTORS_AVAILABLE') || 'No Open Connectors available'
          : t('NO_TOOLS_AVAILABLE') || 'No tools available';

  const selectedLabel =
    toolType === 'mcp_server'
      ? selectedCountForCurrentTab === 1
        ? t('MCP_SERVER_SELECTED') || 'MCP server selected'
        : t('MCP_SERVERS_SELECTED') || 'MCP servers selected'
      : selectedCountForCurrentTab === 1
        ? t('TOOL_SELECTED') || 'tool selected'
        : t('TOOLS_SELECTED') || 'tools selected';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        hideClose
        className={cn(
          'flex h-[min(92dvh,720px)] w-[calc(100vw-2rem)] max-w-[640px] flex-col gap-0 overflow-hidden rounded-3xl p-0 shadow-2xl',
          ui.dialog
        )}
      >
        <DialogTitle className="sr-only">{t('ADD_TOOL') || 'Add tool'}</DialogTitle>

        <div className={cn('relative min-w-0 shrink-0 px-6 pb-5 pt-6', ui.header)}>
          <button
            type="button"
            onClick={() => handleClose(false)}
            className={cn('absolute right-4 top-4 rounded-full p-1.5 transition-colors', ui.close)}
            aria-label={t('CLOSE', 'Close')}
          >
            <X className="h-4 w-4" />
          </button>

          <p className={cn('pr-8 font-sans text-2xl font-bold leading-tight', ui.title)}>
            {t('ADD_TOOL') || 'Add tool'}
          </p>
          <p className={cn('mt-2 pr-8 font-sans text-sm leading-5', ui.subtitle)}>
            {toolType === 'mcp_server'
              ? t('BROWSE_AND_SELECT_MCP_SERVERS_FOR_AGENT') ||
                'Browse and select existing MCP servers to add to your agent.'
              : toolType === 'composio'
                ? t('BROWSE_AND_SELECT_CONNECTOR_TOOLS_FOR_AGENT') ||
                  'Browse and select existing Connectors to add to your agent.'
                : toolType === 'open_connector'
                  ? t('BROWSE_AND_SELECT_OPEN_CONNECTOR_TOOLS_FOR_AGENT') ||
                    'Browse and select existing Open Connectors to add to your agent.'
                  : t('BROWSE_AND_SELECT_TOOLS_FOR_AGENT') ||
                    'Browse and select existing tools to add to your agent.'}
          </p>

          <Tabs
            value={toolType}
            onValueChange={(value) => setToolType(value as AgentConfigToolType)}
            className="mt-5 min-w-0"
          >
            <div className="overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsList
                className={cn(
                  'h-11 w-max min-w-full flex-nowrap justify-start overflow-visible rounded-2xl border p-1',
                  ui.tabList
                )}
              >
                <TabsTrigger
                  value="composio"
                  className={cn(
                    'h-full flex-1 shrink-0 rounded-xl bg-transparent px-2 text-xs shadow-none data-[state=active]:shadow-none sm:px-4 sm:text-sm',
                    toolType === 'composio' && activeTabText,
                    ui.tab,
                    ui.tabActive
                  )}
                >
                  {t('CONNECTOR') || 'Connector'}
                </TabsTrigger>
                <TabsTrigger
                  value="mcp_server"
                  className={cn(
                    'h-full flex-1 shrink-0 rounded-xl bg-transparent px-2 text-xs shadow-none data-[state=active]:shadow-none sm:px-4 sm:text-sm',
                    toolType === 'mcp_server' && activeTabText,
                    ui.tab,
                    ui.tabActive
                  )}
                >
                  {t('MCP_SERVER') || 'MCP Server'}
                </TabsTrigger>
                <TabsTrigger
                  value="api"
                  className={cn(
                    'h-full flex-1 shrink-0 rounded-xl bg-transparent px-2 text-xs shadow-none data-[state=active]:shadow-none sm:px-4 sm:text-sm',
                    toolType === 'api' && activeTabText,
                    ui.tab,
                    ui.tabActive
                  )}
                >
                  {t('API') || 'API'}
                </TabsTrigger>
                {isOpenConnectorEnabled ? (
                <TabsTrigger
                  value="open_connector"
                  className={cn(
                    'h-full flex-1 shrink-0 rounded-xl bg-transparent px-2 text-xs shadow-none data-[state=active]:shadow-none sm:px-4 sm:text-sm',
                    toolType === 'open_connector' && activeTabText,
                    ui.tab,
                    ui.tabActive
                  )}
                >
                  {t('OPEN_CONNECTORS') || 'Open Connectors'}
                </TabsTrigger>
                ) : null}
              </TabsList>
            </div>
          </Tabs>

          <div className="relative mt-3">
            <Search
              className={cn(
                'pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2',
                ui.searchIcon
              )}
            />
            <input
              type="search"
              placeholder={t('SEARCH_TOOLS') || 'Search tools...'}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className={cn(
                'h-12 w-full rounded-2xl border pl-11 pr-4 text-sm outline-none transition-colors',
                ui.search
              )}
            />
          </div>
        </div>

        {selectedCountForCurrentTab > 0 && (
          <div className={cn('flex shrink-0 flex-wrap gap-2 border-b px-6 pb-4 pt-4', ui.chipWrap)}>
            {selectedTools.map((tool) => (
              <button
                key={tool.id}
                type="button"
                onClick={() => onRemoveTool(tool.id)}
                className={cn(
                  'inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  ui.chip
                )}
              >
                <span className="truncate">{tool.name}</span>
                <X className="h-3 w-3 shrink-0" />
              </button>
            ))}
          </div>
        )}

        <div
          ref={listContainerRef}
          className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain px-6 py-4"
        >
          {isLoading || isPromotedLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <ToolRowSkeleton key={index} fieldClass={ui.field} />
            ))
          ) : (
            <>
              {filteredTools.length === 0 ? (
                <div className="flex h-full min-h-56 flex-col items-center justify-center text-center">
                  <div
                    className={cn(
                      'mb-3 flex h-14 w-14 items-center justify-center rounded-full',
                      ui.emptyIcon
                    )}
                  >
                    <Wrench className="h-6 w-6" />
                  </div>
                  <p className={cn('text-sm font-medium', ui.body)}>{emptyMessage}</p>
                </div>
              ) : (
                filteredTools.map((tool) => {
                  const isSelected = selectedToolIds.includes(tool.id);

                  return (
                    <button
                      key={tool.id}
                      type="button"
                      onClick={() => (isSelected ? onRemoveTool(tool.id) : onAddTool(tool))}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-colors',
                        isSelected ? ui.rowSelected : ui.rowIdle
                      )}
                    >
                      {showConnectorLogos ? (
                        <ConnectorToolLogo
                          tool={tool}
                          logoClass="border border-grey-100 bg-true-white p-1.5 dark:border-border dark:bg-card"
                          idleClass={isSelected ? ui.iconSelected : ui.iconIdle}
                        />
                      ) : (
                        <div
                          className={cn(
                            'flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl',
                            isSelected ? ui.iconSelected : ui.iconIdle
                          )}
                        >
                          <Wrench className="h-5 w-5" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className={cn('truncate font-medium', ui.body)}>{tool.name}</div>
                        <div className={cn('line-clamp-2 break-words text-sm', ui.muted)}>
                          {tool.description || t('NO_DESCRIPTION') || 'No description'}
                        </div>
                      </div>
                      <span
                        className={cn(
                          'inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-semibold',
                          isSelected ? ui.pillRemove : ui.pillIdle
                        )}
                      >
                        {isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                        {isSelected ? t('REMOVE') || 'Remove' : t('ADD') || 'Add'}
                      </span>
                    </button>
                  );
                })
              )}

              {isFetchingNextPage &&
                Array.from({ length: 2 }).map((_, index) => (
                  <ToolRowSkeleton key={`tool-load-more-${index}`} fieldClass={ui.field} />
                ))}

              {hasNextPage && (
                <div ref={loadMoreRef} className="h-1 w-full shrink-0" aria-hidden />
              )}
            </>
          )}
        </div>

        <div className={cn('shrink-0 border-t px-6 py-4', ui.footer)}>
          <p className={cn('text-sm', ui.muted)}>
            {selectedCountForCurrentTab} {selectedLabel}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
